import { getConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import { forceLogoutSessionExpired } from "@/domains/shared/services/sessionExpired";
import { isSessionSupersededDetail } from "@/domains/shared/services/sessionSupersededDetail";
import { resetCsrfToken } from "@/domains/shared/services/setupCsrfInterceptor";
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

type RetryableConfig = InternalAxiosRequestConfig & {
  _tokenRefreshRetry?: boolean;
  _skipTokenRefresh?: boolean;
};

const REFRESH_PATH = "/auth/refresh";

let refreshInFlight: Promise<"ok" | "superseded" | "failed"> | null = null;

function shouldAttemptRefresh(error: AxiosError): boolean {
  const config = error.config as RetryableConfig | undefined;
  if (!config || config._tokenRefreshRetry || config._skipTokenRefresh) {
    return false;
  }

  const url = config.url ?? "";
  if (
    url.includes(REFRESH_PATH) ||
    url.includes("/auth/login") ||
    url.includes("/auth/register") ||
    url.includes("/auth/logout")
  ) {
    return false;
  }

  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (isSessionSupersededDetail(detail)) {
    return false;
  }

  return error.response?.status === 401;
}

async function refreshAccessToken(api: AxiosInstance): Promise<"ok" | "superseded" | "failed"> {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post(REFRESH_PATH, null, { _skipTokenRefresh: true } as RetryableConfig)
      .then(() => "ok" as const)
      .catch((error: AxiosError) => {
        const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
        if (isSessionSupersededDetail(detail)) {
          return "superseded" as const;
        }

        return "failed" as const;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

export function setupTokenRefreshInterceptor(api: AxiosInstance): void {
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (!shouldAttemptRefresh(error)) {
        return Promise.reject(error);
      }

      const original = error.config as RetryableConfig | undefined;
      if (!original) {
        return Promise.reject(error);
      }

      const refreshResult = await refreshAccessToken(api);
      if (refreshResult !== "ok") {
        if (refreshResult === "failed" && getConnectivityOnline()) {
          await forceLogoutSessionExpired();
        }

        return Promise.reject(error);
      }

      resetCsrfToken();
      original._tokenRefreshRetry = true;
      return api.request(original);
    },
  );
}
